const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const TARGET_IMAGE_SIZE_BYTES = 4.8 * 1024 * 1024;

export const imageNeedsCompression = (file) =>
  file.type.startsWith("image/") && file.size > MAX_IMAGE_SIZE_BYTES;

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("The browser could not encode this image."));
      },
      "image/jpeg",
      quality
    );
  });

const safeBaseName = (name) =>
  name
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "") || "portfolio-image";

const loadImageWithBrowserOrientation = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("This image format could not be read in the browser."));
    };
    image.src = url;
  });

export const compressPortfolioImage = async (file, onProgress = () => {}) => {
  if (!imageNeedsCompression(file)) return file;

  const image = await loadImageWithBrowserOrientation(file);

  let maxDimension = 2560;
  let quality = 0.88;

  for (let attempt = 0; attempt < 10; attempt += 1) {
    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Image compression is unavailable in this browser.");

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, quality);
    onProgress(Math.min(95, Math.round(((attempt + 1) / 10) * 100)));

    if (blob.size <= TARGET_IMAGE_SIZE_BYTES) {
      onProgress(100);
      return new File([blob], `${safeBaseName(file.name)}-compressed.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    }

    if (quality > 0.52) quality -= 0.1;
    else {
      maxDimension = Math.max(800, Math.round(maxDimension * 0.78));
      quality = 0.78;
    }
  }

  throw new Error("Could not reduce this image below 5 MB. Please choose a smaller image.");
};

export { MAX_IMAGE_SIZE_BYTES };
